<template>
  <v-container pa-0 fluid grid-list-lg data-qa="list_selector">
    <v-layout row wrap fill-height>
      <v-flex v-if="getlistVisible()" xs12 sm3>
        <!-- The slot appears above the slot if on mobile and not collapsing -->
        <slot v-if="getSmallAndSelected() && !collapseOnMobile" />
        <bm-text
          v-if="searchable"
          v-model="searchFilter"
          :label="searchLabel"
          prepend-icon="search"
          :placeholder="searchPlaceholder"
          :disabled="disabled"
        >
        </bm-text>

        <template v-for="heading in headings">
          <h3 v-if="heading" :key="heading" class="bm-headline">
            {{ heading }}
          </h3>
          <v-divider v-if="heading" :key="heading + 'divider'" />

          <v-list :key="heading + 'list'" class="bm-list">
            <template
              v-for="menuItem in getFilteredAndLimitedListItems(heading)"
            >
              <v-list-item
                :id="getItemText(menuItem)"
                :key="getItemValue(menuItem).toString()"
                :disabled="disabled"
                :class="{ active: value === getItem(menuItem) }"
                @click="$emit('input', getItem(menuItem))"
              >
                <v-list-item-content class="bm-body">
                  <v-list-item-title class="d-flex">
                    <span class="list-selector-title">{{
                      getItemText(menuItem)
                    }}</span>
                    <div>
                      <slot name="append-name" :item="menuItem" />
                    </div>
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-for="text in getItemSubtitle(menuItem)"
                    :key="text + 'subtitle'"
                    >{{ text }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider :key="getItemValue(menuItem) + 'divider'" />
            </template>
            <template v-if="getFilteredListShowMore(heading)">
              <v-list-item
                :key="heading.toString() + 'show_more'"
                @click="showAll = true"
              >
                <v-list-item-content class="bm-body">
                  <bm-btn
                    btn-class="secondary"
                    :t="$m.keys.filter_show_all"
                  ></bm-btn>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </template>
      </v-flex>
      <v-flex v-if="value" xs12 sm9>
        <div
          v-if="getSmallAndSelected() && collapseOnMobile && !hideBackButton"
          class="bm-list-back"
        >
          <a @click="$emit('input', null)">
            <v-icon>arrow_back_ios</v-icon>
            <span>{{ returnLabel }}</span></a
          >
        </div>
        <slot v-if="!(getSmallAndSelected() && !collapseOnMobile)" />
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script lang="ts">
import type { ListItemWithHeader } from "@/components/controls/listselector/BmListSelectorTypes";
import messages from "@/plugins/i18n";
import type { VuetifyPropertyArg } from "@/components/utils/vuetifyHelpers";
import { getPropertyFromItem } from "@/components/utils/vuetifyHelpers";
import debounce from "lodash-es/debounce";
import BmBtn from "@/components/controls/input/BmBtn.vue";
import type { PropType } from "vue";
import { defineComponent } from "vue";

export default defineComponent({
  name: "BmListSelector",
  components: { BmBtn },
  props: {
    returnLabel: {
      type: String,
      default: messages.back,
    },
    searchLabel: {
      type: String,
      default: messages.search,
    },
    searchPlaceholder: {
      type: String,
      default: "",
    },
    headings: {
      type: Array as PropType<string[]>,
      default: () => [""],
    },
    items: {
      type: Array as PropType<object[] | ListItemWithHeader[]>,
      default: () => [],
    },
    itemText: {
      type: [Function, String] as PropType<VuetifyPropertyArg>,
      required: true,
    },
    itemValue: {
      type: [Function, String, Object, Number] as PropType<VuetifyPropertyArg>,
      required: true,
    },
    maxItems: {
      type: Number,
      default: () => 10,
    },
    itemSubtitle: {
      type: Object as PropType<VuetifyPropertyArg>,
      default: undefined,
    },
    value: {
      type: Object as PropType<object | string | null>,
      required: false,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    defaultSelectFirstItem: {
      type: Boolean,
      default: true,
    },
    hideList: {
      type: Boolean,
      default: false,
    },
    hideBackButton: {
      type: Boolean,
      default: false,
    },
    collapseOnMobile: {
      type: Boolean,
      default: true,
    },
    sortBy: {
      type: Function as PropType<
        ((a: object, b: object) => number) | undefined
      >,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchFilter: "" as string,
      firstLoad: true,
      showAll: false as boolean,
      selectNewItem: debounce(async function (this: any) {
        let filteredItems = this.getFilteredListItems();
        if (this.$vuetify.breakpoint.smAndUp && filteredItems[0]) {
          this.$emit("input", this.getItem(filteredItems[0]));
        } else {
          this.$emit("input", null);
        }
      }, 300),
    };
  },
  watch: {
    items: {
      // @ts-ignore
      handler: "itemsWatch",
      immediate: true,
    },
    searchFilter: {
      // @ts-ignore
      handler: "searchFilterWatch",
    },
    value: {
      // @ts-ignore
      handler: "valueWatch",
    },
  },
  methods: {
    itemsWatch(val: object[] | ListItemWithHeader[]) {
      // Default select the first item in 'desktop mode' and on first load only
      if (
        this.items.length &&
        this.firstLoad &&
        this.defaultSelectFirstItem &&
        this.$vuetify.breakpoint.smAndUp
      ) {
        this.firstLoad = false;
        for (const heading of this.headings) {
          for (const item of val) {
            this.$emit("input", this.getItem(item));
            return;
          }
        }
      }
    },
    searchFilterWatch() {
      this.showAll = false;
      this.selectNewItem();
    },
    valueWatch() {
      if (this.value === null) this.selectNewItem();
    },
    isItemWithHeader(
      item: object | ListItemWithHeader
    ): item is ListItemWithHeader {
      return (item as ListItemWithHeader).heading !== undefined;
    },
    getItem(item: object | ListItemWithHeader) {
      return this.isItemWithHeader(item) ? item.item : item;
    },
    getItemText(item: object | ListItemWithHeader): string {
      return this.isItemWithHeader(item)
        ? getPropertyFromItem(item.item, this.itemText)
        : getPropertyFromItem(item, this.itemText);
    },
    getItemSubtitle(item: object | ListItemWithHeader): string[] {
      const subtitle = this.isItemWithHeader(item)
        ? getPropertyFromItem(item.item, this.itemSubtitle)
        : getPropertyFromItem(item, this.itemSubtitle);

      /** Prevent getPropertyFromItem returning just an object, and make sure
       *  the title is an array when looping through subtitle entries.
       **/
      if (!this.itemSubtitle) {
        return [];
      }

      return Array.isArray(subtitle) ? subtitle : [subtitle];
    },
    getItemValue(item: object | ListItemWithHeader): string {
      return this.isItemWithHeader(item)
        ? getPropertyFromItem(item.item, this.itemValue)
        : getPropertyFromItem(item, this.itemValue);
    },
    getFilteredAndLimitedListItems(heading?: string) {
      return this.getFilteredListItems(heading).slice(
        0,
        this.showAll ? Number.MAX_SAFE_INTEGER : this.maxItems
      );
    },
    getFilteredListItems(heading?: string) {
      return this.items
        .filter(
          (o) => !heading || !this.isItemWithHeader(o) || o.heading === heading
        )
        .filter((o) =>
          this.getItemText(o)
            .toLocaleLowerCase()
            .includes(this.searchFilter.toLocaleLowerCase())
        )
        .sort(this.sortBy);
    },
    getFilteredListShowMore(heading?: string): boolean {
      return (
        !this.showAll &&
        this.getFilteredListItems(heading).length > this.maxItems
      );
    },
    getlistVisible(): boolean {
      return (!this.hideList && this.$vuetify.breakpoint.smAndUp) ||
        (!this.value && this.items) ||
        (!this.$vuetify.breakpoint.smAndUp && !this.collapseOnMobile)
        ? true
        : false;
    },
    getSmallAndSelected() {
      return !!(this.$vuetify.breakpoint.xs && this.value && this.items);
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

.bm-list.theme--light.v-list
  background-color: transparent
  margin-top: 15px
  margin-bottom: 20px

.bm-list .active
  background-color: $bm-color-brand02

:deep(.bm-list .v-list-item.active .v-list-item__subtitle)
  color: white !important

:deep(.bm-list .v-list-item.active .v-list-item__title)
  color: white !important

:deep(.bm-list .v-list-item .v-list-item__title)
  color: $bm-color-text02

.bm-list-back
  margin-bottom: 1em
  margin-top: 1em

.bm-list-back span
  text-decoration: underline
  vertical-align: middle

.list-selector-title
  flex: 1
  min-width: 0
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis

/* or some value */
</style>
